import { db, storage } from "./config"
import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc, deleteDoc } from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { getListingsByCategories } from "./listings"

export const createCategory = async (image, name, subCategories) => {
    try {
        // Reference to the "categories" collection
        const categoriesRef = collection(db, "categories")
        let imageURL

        if (image !== null) {
            const storageRef = ref(storage, `images/categories/${name}`)
            await uploadBytes(storageRef, image)
            imageURL = await getDownloadURL(storageRef)
        }

        // Add a new document with a generated ID
        const docRef = await addDoc(categoriesRef, {
            image: image ? imageURL : null,
            name: name,
            subCategories: subCategories,
            created_at: new Date(),
            // Live as soon as an admin adds it. Every shop-facing read asks
            // for state === 'active' only, so defaulting to 'pending' meant
            // a category an admin had just created never appeared until
            // they found it in the pending list and activated it -- read as
            // the platform being slow rather than as a second step. Making
            // one pending again is still there for taking a category down.
            state: 'active'
        })

        console.log("Document written with ID: ", docRef.id)
    } catch (e) {
        console.error("Error adding document: ", e)
    }
}

export const updateCategory = async (updatedData) => {
    try {
        // Reference to the specific category document
        const categoryRef = doc(db, "categories", updatedData.id)
        const categorySnapshot = await getDoc(categoryRef)
        const categoryData = categorySnapshot.data()
        let imageURL = categoryData.image

        if (!(updatedData.picture === undefined || updatedData.picture === null)) {
            const storageRef = ref(storage, `images/categories/${updatedData.name}`)
            await uploadBytes(storageRef, updatedData.picture)
            imageURL = await getDownloadURL(storageRef)
        }

        const newData = {
            name: updatedData.name,
            image: imageURL,
            subCategories: updatedData.subCategories,
            state: updatedData.state ? updatedData.state : categoryData.state
        }

        // Update the document with new data
        await updateDoc(categoryRef, newData)

        console.log("Document updated with ID: ", updatedData.id)
    } catch (e) {
        console.error("Error updating document: ", e)
    }
}

/**
 * Takes a category live, or off the platform.
 *
 * A single-field write rather than updateCategory, which rebuilds name,
 * image and subCategories from whatever the caller passed -- a status flip
 * has no business touching any of those, and passing a partial object there
 * would blank them.
 *
 * Shop-facing reads ask for state === 'active' only, so this is the switch
 * that decides whether shoppers ever see the category. There was no way to
 * throw it from the admin UI: the buttons that called updateCategory with a
 * state lived on a page that was never routed, which left categories
 * created before they defaulted to active stranded and invisible.
 */
export const setCategoryState = async (categoryId, state) => {
    try {
        await updateDoc(doc(db, "categories", categoryId), { state })
        return true
    } catch (e) {
        console.error("Error updating category state: ", e)
        return false
    }
}

export const deleteCategory = async (categoryId) => {
    try {
        // Reference to the specific category document
        const categoryRef = doc(db, "categories", categoryId);

        // Delete the document
        await deleteDoc(categoryRef);

        console.log("Document deleted with ID: ", categoryId);
    } catch (e) {
        console.error("Error deleting document: ", e);
    }
}

export const createCategoryWithImage = async (categoryId, updatedData) => {
    try {
        // Reference to the specific category document
        const categoryRef = doc(db, "categories", categoryId)

        // Update the document with new data
        await updateDoc(categoryRef, updatedData)

        console.log("Document updated with ID: ", categoryId)
    } catch (e) {
        console.error("Error updating document: ", e)
    }
}

export const getAllCategories = async (states = ["active"]) => {
    try {
        // Reference to the "categories" collection
        const categoriesRef = collection(db, "categories")

        const categories = []

        // Create a query to filter categories by state
        for (let state of states) {
            const q = query(categoriesRef, where("state", "==", state))
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach(async (doc) => {
                // const listings = await getListingsByCategories([doc.id], null)
                categories.push({ id: doc.id, listingsCount: 0, ...doc.data() })
            })
        }

        return categories // Return the array of categories
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getCategoryById = async (categoryId) => {
    try {
        // Reference to the "categories" collection
        const categoriesRef = doc(db, "categories", categoryId)
        const querySnapshot = await getDoc(categoriesRef)

        return querySnapshot.data()
    } catch (e) {
        console.error("Error getting category: ", e)
        return null // Return null in case of error
    }
}

export const checkCategoryExists = async (categoryName) => {
    try {
        const categoriesRef = collection(db, "categories")
        const q = query(categoriesRef, where("name", "==", categoryName))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
            return true // Category exists
        } else {
            return false // Category does not exist
        }
    } catch (error) {
        console.error("Error checking category:", error)
    }
}